<script setup>
  import { ref, computed, watchEffect } from 'vue';
  import { useSheetController } from '@/composables/useSheetsController';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import SheetResizer from '@/components/SheetResizer.vue';

  //TODO: Return focus to sidebar button on close;

  const props = defineProps({
    type: {
      type: String,
      required: true
    },
    resizeable: {
      type: Boolean,
      default: true
    },
    backdrop: {
      type: Boolean,
      default: true
    },
    startingHeightState: {
      type: String,
      default: 'maximized'
    },
    position: {
      type: String,
      default: 'primary'
    }
  });

  const isDesktop = useMediaQuery('(min-width: 840px)');

  let sheet = ref(null);
  let sheetPosition = ref(props.position);
  let sheetStartingHeightState = ref(props.startingHeightState);
  let sheetHeightState = ref(sheetStartingHeightState.value);
  let contentTransitionName = ref('content-change-slide');

  let resetSheetHeight = ref(false);

  const currentState = computed(() => useSheetController.sheets[props.type].open ? 'open' : 'closed');  
  const dataLayer = computed(() => useSheetController.sheetOrder[sheetPosition.value].indexOf(props.type));
  const useBringToFrontButton = computed(() => currentState.value === 'open' && dataLayer.value !== 0);
  const teleportSheet = computed(() => isDesktop.value && props.position === 'secondary');
  const sheetClasses = computed(() => ['jwx-sheet', `jwx-sheet--${props.position}`]);

  const currentZIndex = computed(() => {
    let sheetMaxPositionIndex = 
      teleportSheet.value
        ? useSheetController.sheetOrder[sheetPosition.value].length
        : useSheetController.sheetOrder[sheetPosition.value].length - 1;

    const zIndex = sheetMaxPositionIndex - dataLayer.value;

    return zIndex < 0 ? 0 : zIndex;
  });  

  function onBeforeEnter() {
    sheetHeightState.value = sheetStartingHeightState.value;
    resetSheetHeight.value = true;
    contentTransitionName.value = 'content-change-opacity'
  }


  function onAfterEnter() {
    contentTransitionName.value = 'content-change-slide';
  }

  function onBeforeLeave() {
  }

  function onAfterLeave() {
    useSheetController.sendToBack(props.type);
    resetSheetHeight.value = false;
  }  

  watchEffect(() => sheetStartingHeightState.value = props.startingHeightState);
  watchEffect(() => {
    useSheetController.sheets[props.type].size = !useSheetController.sheets[props.type].open ? '' : sheetHeightState.value;
    useSheetController.updateBackdropStatus();
  });
</script>

<template>
  <Teleport to=".jwx-map-ui" :disabled="!teleportSheet">
    <Transition
      name="sheet"
      @before-enter="onBeforeEnter"
      @after-enter="onAfterEnter"
      @before-leave="onBeforeLeave"
      @after-leave="onAfterLeave"
    >
        <div
          v-show="currentState === 'open'"
          :class="sheetClasses"
          :data-state="currentState" 
          :style="{zIndex: currentZIndex}" 
          :data-layer="useSheetController.sheetOrder[props.position].indexOf(props.type)"
          :data-height-state="sheetHeightState"
          key="prop.type"
          ref="sheet"
        >
          <SheetResizer
            v-if="props.resizeable && !teleportSheet"
            v-model="sheetHeightState"
            :sheet="sheet" 
            :type="props.type" 
            :resetSheetHeight="resetSheetHeight"
          />
          <slot :transtionName="contentTransitionName"></slot> 
          <button 
            @click.prevent="useSheetController.bringToFront(props.type)"
            v-if="useBringToFrontButton" 
            class="jwx-sheet__bring-to-front"
          >
            <div class="sr-only">Bring panel to front.</div>
          </button>
        </div>
    </Transition>
  </Teleport>
</template>